// shell/Backdrop/DeepSignalBackdrop.tsx — the "Register" CSS backdrop.
//
// Register paper seen from above: an accent-tinted stock, one long diagonal
// ramp of light across it, and 24px ruling that shows ONLY in the margins.
// Authored once and rendered at an `intensity` scalar: full on auth (1.0), a
// dimmed ambient behind the app feed (the user's bgIntensity pref).
//
// Replaces "Sundown Signal" (a banded retro sun on a magenta perspective neon
// grid). That composition was the loudest generated-art signal in the app, and
// it needed a hand-authored 800px blackout strip down the middle of the screen
// purely so body text on top of it stayed readable — decoration fighting the
// interface. Here the rules are masked OUT of the reading column instead, using
// the same --rail-w / --right-w custom properties ShellLayout already publishes,
// so containment self-corrects when the rail collapses (the old strip was
// hard-coded to ±400px of centre and drifted off-centre the moment it did).
//
// Rules baked in:
//   • one accent-derived ramp, LINEAR — never a radial bloom, never a hue shift
//   • nothing is painted behind the reading column at any tier
//   • no runtime filter:blur on a full-bleed layer; softness is authored
//   • constant grain + vignette, so 'off' is still a designed void
//   • the only motion is light, not shape, and only at the 'high' tier

import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { deriveRegister } from '@/theme/presets/deepSignal';

export interface DeepSignalBackdropProps {
  tier: 'high' | 'low' | 'off';
  /** Freeze motion (reduce-motion). */
  paused: boolean;
  /** 0.15–1.0 — showpiece↔ambient scalar (auth 1.0, shell = bgIntensity pref). */
  intensity: number;
}

type Reg = ReturnType<typeof deriveRegister>;

const Field = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

/* The stock: one long LINEAR ramp corner to corner. This is the whole of the
   "alive" quality — a sheet lit from one side, not an object floating on a sky. */
const Tide = styled.div<{ $c: Reg; $k: number; $anim: boolean }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    128deg,
    ${({ $c }) => $c.tideFrom} 0%,
    transparent 46%,
    ${({ $c }) => $c.tideTo} 100%
  );
  opacity: ${({ $k }) => 0.45 + $k * 0.55};
  ${({ $anim }) =>
    $anim &&
    `
    @keyframes reg-tide { 50% { opacity: var(--reg-tide-hi); } }
    animation: reg-tide 90s ease-in-out infinite alternate;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  `}
`;

/* Margin ruling on the feed's own 24px module, masked to the gutters by the
   grid track widths so it can never sit under a line of text. The mask is built
   from the SAME custom properties ShellLayout feeds grid-template-columns, so
   it follows the rail's collapsed width for free. */
const Rules = styled.div<{ $c: Reg; $k: number }>`
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    0deg,
    transparent 0 23px,
    ${({ $c }) => $c.rule} 23px 24px
  );
  opacity: ${({ $k }) => 0.35 + $k * 0.65};
  --reg-gap: 24px;
  -webkit-mask-image: linear-gradient(
    90deg,
    #000 0 var(--rail-w, 240px),
    transparent calc(var(--rail-w, 240px) + var(--reg-gap)),
    transparent calc(100% - var(--right-w, 340px) - var(--reg-gap)),
    #000 calc(100% - var(--right-w, 340px)) 100%
  );
  mask-image: linear-gradient(
    90deg,
    #000 0 var(--rail-w, 240px),
    transparent calc(var(--rail-w, 240px) + var(--reg-gap)),
    transparent calc(100% - var(--right-w, 340px) - var(--reg-gap)),
    #000 calc(100% - var(--right-w, 340px)) 100%
  );
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  /* Gentle corner-only containment: must NOT darken the gutters, which are the
     card-free zones where the ruling is meant to be visible. */
  background: radial-gradient(
    150% 125% at 50% 62%,
    transparent 74%,
    ${({ theme }) => theme.color.bgRoot} 100%
  );
  opacity: ${({ theme }) => (theme.mode === 'light' ? 0.25 : 0.4)};
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${({ theme }) => (theme.mode === 'light' ? 0.02 : 0.03)};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

export function DeepSignalBackdrop({ tier, paused, intensity }: DeepSignalBackdropProps) {
  const theme = useTheme();
  const colors = useMemo(
    () => deriveRegister(theme.color.accentCyan, theme.mode),
    [theme.color.accentCyan, theme.mode],
  );

  if (tier === 'off') {
    // Constant frame only — designed void, not a flat rectangle.
    return (
      <Field aria-hidden data-testid="backdrop-off-frame">
        <Vignette />
        <Grain data-testid="ss-grain" />
      </Field>
    );
  }

  const k = Math.max(0.15, Math.min(1, intensity));
  // 'high' differs from 'low' by breathing the ramp's opacity over 90s: the
  // light in the room shifts, no shape moves. Before 1.25.00 resolveTier's
  // 'high' was documented as "same as low for now", which was a tier that
  // claimed a difference it did not have.
  const animate = tier === 'high' && !paused;

  return (
    <Field
      aria-hidden
      data-testid="backdrop-register"
      style={{ ['--reg-tide-hi' as string]: String(Math.min(1, 0.45 + k * 0.55 + 0.12)) }}
    >
      <Tide $c={colors} $k={k} $anim={animate} data-testid="reg-tide" />
      <Rules $c={colors} $k={k} />
      <Vignette />
      <Grain data-testid="ss-grain" />
    </Field>
  );
}
